import React, { useState } from 'react';
import Saral from '@/images/Saral-bg.png';
import { fetchMultiBrandAccounts } from '@/redux/user/userActions';
import { addNewConnectedBrand } from '@/services/multiBrand.service';
import { getStorageObject } from '@/utils/global';
import {
  INSERT_DATA_IN_MULTIBRAND_TOKENS_DATA,
  MULTIBRAND_TOKENS_ARRAY,
  MULTIBRAND_TOKENS_KEY,
} from '@/utils/multibrand-utils';
import { validateLogin } from '@/validations/auth';
import { NotificationManager } from 'react-notifications';
import { useDispatch } from 'react-redux';

import { MultibrandTokenDataType } from '@/types/common';
import ButtonComponent from '@/components/common-components/ButtonComponent/ButtonComponent';
import InputComponent from '@/components/common-components/InputComponent/InputComponent';
import ModalComponent from '@/components/common-components/ModalComponent/ModalComponent';
import { VisibilityIcon, VisibilityOffIcon } from '@/components/Icons';

interface Props {
  show: boolean;
  onClose: () => void;
  setSelectedBrand: React.Dispatch<React.SetStateAction<string | null>>;
}

const AddAccountModal: React.FC<Props> = ({ show, onClose, setSelectedBrand }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const errorDefaultValue = {
    email: '',
    password: '',
  };

  const [errors, setErrors] = useState(errorDefaultValue);
  const [showPassword, setShowPassword] = useState(false);
  const [disableSubmitButton, setDisableSubmitButton] = useState(false);

  const dispatch = useDispatch();

  const handleClose = () => {
    setErrors(errorDefaultValue);
    setEmail('');
    setPassword('');
    setShowPassword(false);
    setDisableSubmitButton(false);
    onClose();
  };

  const addNewBrandHandler = async () => {
    if (disableSubmitButton) {
      return;
    }

    const body = { email, password };
    setDisableSubmitButton(true);
    const isValid = await validateLogin(body, setErrors, password);
    if (!isValid) {
      setDisableSubmitButton(false);
      return;
    }

    const response = await addNewConnectedBrand({ ...body, tokens: getStorageObject(MULTIBRAND_TOKENS_KEY) || [] });
    if (response?.status && response.data.tokenData) {
      const finalData = INSERT_DATA_IN_MULTIBRAND_TOKENS_DATA(response?.data?.tokenData);
      const newTokensArray = finalData.map((token: MultibrandTokenDataType) => token?.token);
      dispatch(fetchMultiBrandAccounts(newTokensArray));
      setSelectedBrand(response?.data?._id);
      NotificationManager.success('Account Added', '', 1000);
      setDisableSubmitButton(false);
      handleClose();
    } else {
      if (response?.data?.message === 'Invalid Password') {
        setErrors((prev) => ({ ...prev, password: 'Incorrect password. Try again.' }));
      } else {
        setErrors((prev) => ({ ...prev, email: response?.data?.message }));
      }
      setDisableSubmitButton(false);
    }
  };

  return (
    <ModalComponent
      show={show}
      onClose={onClose}
      outsideClickClose={false}
      bodyStyle={{ padding: 0 }}
      style={{ padding: 32 }}
      width={424}
      topCross>
      <div className="h-fit flex flex-col center">
        <img src={Saral} alt="saral" width={160} className="saral-logo" />
        <h2 className="mt-6 text-2xl font-medium text-default">Add an account</h2>
        <p className="text-base font-normal text-subdued text-center">
          Log in with an existing account to link it to your profile.
        </p>

        <div className="w-full my-6">
          <InputComponent
            label="Email"
            required
            placeholder="Enter your email"
            value={email}
            onChange={(e) => {
              setErrors(errorDefaultValue);
              setEmail(e.target.value);
            }}
            style={{ borderColor: errors?.email ? '#e64848' : '#d0d5dd' }}
            error={errors.email}
            containerClassName="mb-5"
          />
          <InputComponent
            label="Password"
            required
            type={showPassword ? 'text' : 'password'}
            placeholder="Enter your password"
            value={password}
            onChange={(e) => {
              setErrors(errorDefaultValue);
              setPassword(e.target.value);
            }}
            right={
              <button type="button" className="show-password-btn" onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
              </button>
            }
            error={errors.password}
          />
        </div>

        <ButtonComponent
          onClick={addNewBrandHandler}
          className="w-full"
          disabled={!email || !password}
          showLoading={disableSubmitButton}>
          Log in
        </ButtonComponent>
      </div>
    </ModalComponent>
  );
};

export default AddAccountModal;
